import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  PaperAirplaneIcon,
  MagnifyingGlassIcon,
  HashtagIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  FaceSmileIcon,
  UsersIcon,
  BellIcon,
  BellSlashIcon,
  PlusIcon,
  Bars3Icon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { UserSession } from '../../types';
import { useChat } from '../../hooks/useChat';
import { useSharedPoll } from '../../hooks/useSharedPoll';
import { useSocket } from '../../contexts/SocketContext';
import EmojiPicker from './EmojiPicker';

// 🎨 Cores consistentes por usuário (baseado no nome)
const USER_COLORS = [
  'from-blue-500 to-blue-600',
  'from-emerald-500 to-emerald-600',
  'from-rose-500 to-rose-600',
  'from-amber-500 to-amber-600',
  'from-violet-500 to-violet-600',
  'from-cyan-500 to-cyan-600',
  'from-pink-500 to-pink-600',
  'from-teal-500 to-teal-600',
  'from-orange-500 to-orange-600',
  'from-sky-500 to-sky-600',
  'from-lime-500 to-lime-600',
  'from-fuchsia-500 to-fuchsia-600',
];

const getUserColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
};

interface Message {
  id: string;
  _id?: string;
  channelId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  userEmail?: string;
  content: string;
  timestamp: Date;
  type: 'text' | 'notification' | 'system';
  mentions?: string[];
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
  replyTo?: string;
  empresaId?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  edited?: boolean;
  editedAt?: Date;
}

interface Channel {
  id: string;
  name: string;
  type: 'general' | 'flow' | 'department' | 'direct';
  icon?: any;
  unreadCount: number;
  lastMessage?: Message;
  members?: string[];
  membersDetails?: Array<{
    id: string;
    nome: string;
    email: string;
    foto?: string;
    online?: boolean;
  }>;
  flowId?: string;
  departmentId?: string;
  otherUserId?: string;
  otherUserName?: string;
  otherUserOnline?: boolean;
}

interface ChatTab extends Channel {
  hasUnread: boolean;
}

interface ChatProps {
  session: UserSession;
  flows?: any[];
  departamentos?: any[];
  colaboradores?: any[];
  // 🔔 Conversa a abrir assim que o chat monta (clique na notificação global)
  initialChannelId?: string;
  // Nome de quem mandou — usado quando a DM ainda não existe na lista da API
  initialChannelNome?: string;
}

const ChatImproved: React.FC<ChatProps> = ({ session, colaboradores = [], initialChannelId, initialChannelNome }) => {
  console.log('🎨 [CHATIMPROVED] Renderizando componente', { session, colaboradores: colaboradores.length });
  
  // Socket.IO para tempo real
  const { socket, isConnected, onlineUsers: socketOnlineUsers } = useSocket();
  
  const [channels, setChannels] = useState<Channel[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChannelList, setShowChannelList] = useState(true);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false); // 📱 Painel de membros mobile
  const [showMembersPanelDesktop, setShowMembersPanelDesktop] = useState(true); // 🖥️ Painel de membros desktop
  const [memberSearch, setMemberSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [mutedChannels, setMutedChannels] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // 🖼️ Prints colados na conversa (Ctrl+V). Ficam aqui até a mensagem ser enviada.
  const [imagensPendentes, setImagensPendentes] = useState<Array<{ name: string; url: string; type: string }>>([]);
  const [subindoImagem, setSubindoImagem] = useState(false);

  // Sistema de Abas
  const [openTabs, setOpenTabs] = useState<ChatTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  // Guarda que a conversa vinda da notificação só é aberta uma vez
  const canalInicialAbertoRef = useRef(false);
  
  // 🆕 UI Estados
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // 🆕 Reply System
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  
  // 🆕 Edit System
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [editInput, setEditInput] = useState('');

  // 🟢 Estado para usuários online (sincronizado com Socket.IO)
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);

  // 🔔 MESSENGER-STYLE: Canais que precisam de resposta (fica destacado até responder)
  const [needsReply, setNeedsReply] = useState<Set<string>>(new Set());
  
  // 🔊 Ref para tocar som de notificação
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  
  // Inicializar áudio de notificação
  useEffect(() => {
    const audio = new Audio('data:audio/wav;base64,UklGRl9vT19teleVJZmYAAEARAABBAAgAZGF0YUlvT19C/3//f/9//3//f/9/AIA=');
    // Usar um som curto de notificação
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0;
      notificationAudioRef.current = audio;
    } catch (e) {
      // AudioContext pode não estar disponível
    }
    return () => {};
  }, []);

  // 🔊 Função para tocar som de notificação (respeita configurações)
  const playNotificationSound = useCallback(() => {
    try {
      const saved = localStorage.getItem('chat_settings');
      const settings = saved ? JSON.parse(saved) : { enabled: true, sound: true };
      if (!settings.enabled || !settings.sound) return;
      
      const volSaved = localStorage.getItem('chat_sound_volume');
      const volume = volSaved ? parseInt(volSaved) / 100 : 0.7;
      
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 880;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(Math.max(0.01, volume * 0.5), ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
    } catch (e) {
      // Som não disponível - não bloquear funcionalidade
    }
  }, []);
  
  // Sincronizar onlineUsers com Socket.IO
  useEffect(() => {
    if (socketOnlineUsers) {
      setOnlineUsers(Array.from(socketOnlineUsers));
    }
  }, [socketOnlineUsers]);

  // 🔇 Carregar canais mutados do localStorage
  useEffect(() => {
    if (session?.usuario?.id) {
      const storageKey = `mutedChannels_${session.usuario.id}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          setMutedChannels(new Set(JSON.parse(saved)));
        } catch (e) {
          console.error('Erro ao carregar canais mutados:', e);
        }
      }
    }
  }, [session?.usuario?.id]);

  // 🔇 Função para mutar/desmutar canal
  const toggleMuteChannel = useCallback((channelId: string) => {
    setMutedChannels(prev => {
      const newMuted = new Set(prev);
      if (newMuted.has(channelId)) {
        newMuted.delete(channelId);
      } else {
        newMuted.add(channelId);
      }
      
      // Salvar no localStorage
      if (session?.usuario?.id) {
        const storageKey = `mutedChannels_${session.usuario.id}`;
        localStorage.setItem(storageKey, JSON.stringify(Array.from(newMuted)));
      }
      
      return newMuted;
    });
  }, [session?.usuario?.id]);

  // Hook de chat
  const {
    channels: channelsFromAPI,
    messages,
    sendMessage,
    refreshMessages,
    loadChannels,
    loadMessages,
    addExternalMessage
  } = useChat();

  // Carregar canais ao montar componente
  useEffect(() => {
    if (session?.empresa?.id && session?.usuario?.id) {
      console.log('📡 [FLOWCHAT] Carregando canais...', {
        empresaId: session.empresa.id,
        usuarioId: session.usuario.id,
        usuarioNome: session.usuario.nome
      });
      loadChannels(session.empresa.id, session.usuario.id);
    } else {
      console.warn('⚠️ [FLOWCHAT] Dados de sessão incompletos:', {
        empresa: session?.empresa?.id,
        usuario: session?.usuario?.id
      });
    }
  }, [session?.empresa?.id, session?.usuario?.id, loadChannels]);

  // Mapear canais da API
  useEffect(() => {
    console.log('🔄 [FLOWCHAT] Canais recebidos da API:', channelsFromAPI.length, channelsFromAPI);
    
    const mappedChannels = channelsFromAPI.map(channel => ({
      ...channel,
      icon: channel.type === 'general' 
        ? ChatBubbleLeftRightIcon
        : channel.type === 'flow'
        ? HashtagIcon
        : channel.type === 'department'
        ? BuildingOfficeIcon
        : ChatBubbleLeftRightIcon,
      unreadCount: channel.unreadCount || 0
    }));
    
    console.log('✅ [FLOWCHAT] Canais mapeados:', mappedChannels.length);
    setChannels(mappedChannels);

    // 🔔 Veio de uma notificação: abre a conversa avisada e SÓ ela.
    // Antes o Geral era aberto junto e disparava um segundo carregamento de
    // mensagens; como o Geral tem muito mais histórico, respondia por último e
    // sobrescrevia o conteúdo — a aba da conversa ficava ativa exibindo o Geral.
    if (initialChannelId && !canalInicialAbertoRef.current) {
      let alvo: Channel | undefined = mappedChannels.find(c => c.id === initialChannelId);

      // DM recém-criada ainda não listada pela API (o canal só passa a existir
      // depois da primeira mensagem): monta o canal na hora para não cair no Geral.
      if (!alvo && initialChannelId.startsWith('dm-')) {
        const novoDM: Channel = {
          id: initialChannelId,
          name: initialChannelNome || 'Mensagem direta',
          type: 'direct',
          icon: ChatBubbleLeftRightIcon,
          unreadCount: 0,
          otherUserName: initialChannelNome,
        };
        setChannels(prev => prev.some(c => c.id === initialChannelId) ? prev : [...prev, novoDM]);
        alvo = novoDM;
      }

      if (alvo) {
        canalInicialAbertoRef.current = true;
        openTabForChannel(alvo);
        return; // não abrir o Geral por cima da conversa pedida
      }
    }

    // Abrir canal geral por padrão na primeira vez
    if (mappedChannels.length > 0 && openTabs.length === 0) {
      const generalChannel = mappedChannels.find(c => c.type === 'general');
      if (generalChannel) {
        openTabForChannel(generalChannel);
      }
    }
  }, [channelsFromAPI, initialChannelId, initialChannelNome]);

  // Função para abrir uma aba
  const openTabForChannel = (channel: Channel) => {
    // Verificar se já está aberta
    const existingTab = openTabs.find(tab => tab.id === channel.id);
    
    if (existingTab) {
      setActiveTabId(channel.id);
      loadMessages(channel.id, { userId: session?.usuario?.id, empresaId: session?.empresa?.id });
    } else {
      const newTab: ChatTab = { ...channel, hasUnread: false };
      setOpenTabs(prev => [...prev, newTab]);
      setActiveTabId(channel.id);
      loadMessages(channel.id, { userId: session?.usuario?.id, empresaId: session?.empresa?.id });
    }

    // Marcar canal como lido
    if (session?.usuario?.id) {
      fetch('/api/chat/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: session.usuario.id, channelId: channel.id, empresaId: session.empresa?.id || 'default' })
      }).catch(err => console.warn('⚠️ Erro ao marcar canal como lido:', err));
      // 🔔 Zerar este canal no notificador global (badge do menu + pílula)
      window.dispatchEvent(new CustomEvent('flowchat:lido', { detail: { channelId: channel.id } }));
    }
    
    // 📱 Auto-fechar sidebar no mobile após selecionar canal
    if (window.innerWidth < 768) { // md breakpoint
      setShowChannelList(false);
    }
  };

  // Função para fechar uma aba
  const closeTab = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const tabIndex = openTabs.findIndex(tab => tab.id === tabId);
    const newTabs = openTabs.filter(tab => tab.id !== tabId);
    
    setOpenTabs(newTabs);
    
    // Se a aba fechada era a ativa, selecionar outra
    if (activeTabId === tabId && newTabs.length > 0) {
      const newActiveIndex = Math.max(0, tabIndex - 1);
      setActiveTabId(newTabs[newActiveIndex].id);
      loadMessages(newTabs[newActiveIndex].id, { userId: session?.usuario?.id, empresaId: session?.empresa?.id });
    } else if (newTabs.length === 0) {
      setActiveTabId(null);
      // 📱 Quando não há abas abertas, garantir que o menu de canais fique visível no mobile
      setShowChannelList(true);
    }
  };

  // Limpar badge de contagem ao abrir aba (mas manter needsReply até responder)
  useEffect(() => {
    if (activeTabId) {
      setOpenTabs(prev => prev.map(tab =>
        tab.id === activeTabId ? { ...tab, hasUnread: false, unreadCount: 0 } : tab
      ));
      // Limpar badge do canal na sidebar também
      setChannels(prev => prev.map(ch =>
        ch.id === activeTabId ? { ...ch, unreadCount: 0 } : ch
      ));
    }
  }, [activeTabId]);

  // Scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 🔌 Socket.IO: join/leave canal ativo + ouvir mensagens em tempo real
  useEffect(() => {
    if (!socket || !isConnected || !activeTabId) return;

    // Entrar na sala do canal ativo
    socket.emit('join_channel', { channel_id: activeTabId });
    console.log('🔌 [SOCKET] Entrou no canal:', activeTabId);

    // Listener de mensagens novas
    const handleNewMessage = (data: any) => {
      console.log('📩 [SOCKET] new_message recebido:', data);
      if (!data?.message) return;

      // Não duplicar se a mensagem é do próprio usuário (já adicionada no sendMessage)
      if (data.message.userId === session?.usuario?.id) {
        console.log('⏭️ [SOCKET] Mensagem própria, ignorando (já no estado local)');
        return;
      }

      if (data.channel_id === activeTabId) {
        // Mensagem no canal ativo → adicionar ao estado
        addExternalMessage(data.message);
        // 🔔 MESSENGER: Marcar que precisa responder (mesmo no canal ativo)
        setNeedsReply(prev => new Set([...prev, data.channel_id]));
      } else {
        // Mensagem em outro canal → incrementar badge + auto-abrir aba
        setChannels(prev => prev.map(ch =>
          ch.id === data.channel_id
            ? { ...ch, unreadCount: (ch.unreadCount || 0) + 1 }
            : ch
        ));
        // 🔔 MESSENGER: Marcar que precisa responder
        setNeedsReply(prev => new Set([...prev, data.channel_id]));
        // 🔊 Tocar som de notificação
        if (!mutedChannels.has(data.channel_id)) {
          playNotificationSound();
        }
        setOpenTabs(prev => {
          const existingTab = prev.find(tab => tab.id === data.channel_id);
          if (existingTab) {
            return prev.map(tab =>
              tab.id === data.channel_id
                ? { ...tab, hasUnread: true, unreadCount: (tab.unreadCount || 0) + 1 }
                : tab
            );
          } else {
            // 🔔 Auto-abrir aba para mensagens diretas (DM)
            const channelInfo = channels.find(ch => ch.id === data.channel_id);
            if (channelInfo && channelInfo.type === 'direct') {
              console.log('🔔 [NOTIF] Auto-abrindo aba (new_message) DM de:', channelInfo.name);
              const newTab: ChatTab = {
                ...channelInfo,
                hasUnread: true,
                unreadCount: 1
              };
              return [...prev, newTab];
            }
            // Se é DM mas canal não existe na lista (primeira vez), criar dinamicamente
            const chId = data.channel_id || '';
            if (chId.startsWith('dm-')) {
              const senderName = data.message?.userName || 'Usuário';
              console.log('🔔 [NOTIF] Criando aba DM dinâmica para:', senderName);
              const dynamicChannel: Channel = {
                id: chId,
                name: senderName,
                type: 'direct',
                icon: ChatBubbleLeftRightIcon,
                unreadCount: 1,
                otherUserId: data.message?.userId,
                otherUserName: senderName,
                otherUserOnline: true
              };
              // Adicionar canal à lista
              setChannels(prevCh => {
                if (!prevCh.find(c => c.id === chId)) {
                  return [...prevCh, dynamicChannel];
                }
                return prevCh;
              });
              const newTab: ChatTab = { ...dynamicChannel, hasUnread: true };
              return [...prev, newTab];
            }
            return prev;
          }
        });
      }
    };

    socket.on('new_message', handleNewMessage);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.emit('leave_channel', { channel_id: activeTabId });
      console.log('👋 [SOCKET] Saiu do canal:', activeTabId);
    };
  }, [socket, isConnected, activeTabId, session?.usuario?.id, addExternalMessage, channels, mutedChannels, playNotificationSound]);

  // 🔢 Buscar contagem de não-lidos periodicamente
  // Compartilhado entre as abas do mesmo navegador (ver utils/sharedPolling):
  // uma busca só serve todas. O "canal ativo" continua sendo por aba.
  useSharedPoll(
    session?.usuario?.id && session?.empresa?.id
      ? `chat-unread:${session.usuario.id}:${session.empresa.id}`
      : null,
    `/api/chat/unread?userId=${session?.usuario?.id}&empresaId=${session?.empresa?.id}`,
    60000,
    (data: any) => {
      if (!data?.success || !data.data) return;
      // ⚠️ O backend devolve um DICIONÁRIO { channelId: count } (chat_storage.contar_nao_lidas),
      // não uma lista. Tratar como array quebrava o callback (forEach undefined) e os
      // badges da sidebar nunca atualizavam pelo polling.
      const unreadMap: Record<string, number> = {};
      Object.entries(data.data as Record<string, number>).forEach(([channelId, count]) => {
        unreadMap[channelId] = Number(count) || 0;
      });
      // Atualizar channels (exceto o canal ativo que acabou de ser marcado como lido)
      setChannels(prev => prev.map(ch => ({
        ...ch,
        unreadCount: ch.id === activeTabId ? 0 : (unreadMap[ch.id] ?? ch.unreadCount ?? 0)
      })));
    }
  );

  // 📡 Socket.IO: ouvir mensagens da empresa inteira (para badges de não-lidos em canais que não estou)
  // 🔔 Auto-abrir abas quando alguém manda mensagem em qualquer canal/departamento/DM
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleChatReceived = (data: any) => {
      // Ignorar mensagens do próprio usuário
      if (data?.userId === session?.usuario?.id) return;
      const channelId = data?.channelId;
      if (!channelId) return;
      
      // 🔔 MESSENGER: Marcar que precisa responder
      setNeedsReply(prev => new Set([...prev, channelId]));
      
      // Se é o canal ativo, não precisa fazer badge (já está vendo)
      if (channelId === activeTabId) return;

      // Incrementar badge de não-lidos no canal na sidebar
      setChannels(prev => prev.map(ch =>
        ch.id === channelId ? { ...ch, unreadCount: (ch.unreadCount || 0) + 1 } : ch
      ));

      // 🔊 Tocar som de notificação
      if (!mutedChannels.has(channelId)) {
        playNotificationSound();
      }

      // 🔔 AUTO-ABRIR ABA para mensagens diretas
      setOpenTabs(prev => {
        const existingTab = prev.find(tab => tab.id === channelId);
        if (existingTab) {
          // Aba já existe → apenas marcar como não lida
          return prev.map(tab =>
            tab.id === channelId
              ? { ...tab, hasUnread: true, unreadCount: (tab.unreadCount || 0) + 1 }
              : tab
          );
        } else {
          // Aba NÃO existe → auto-abrir se for DM
          const channelInfo = channels.find(ch => ch.id === channelId);
          if (channelInfo && channelInfo.type === 'direct') {
            console.log('🔔 [NOTIF] Auto-abrindo aba para DM de:', channelInfo.name);
            const newTab: ChatTab = {
              ...channelInfo,
              hasUnread: true,
              unreadCount: 1
            };
            return [...prev, newTab];
          }
          // 🔔 Se é DM mas canal não existe na lista (primeira conversa), criar dinamicamente
          if (channelId.startsWith('dm-')) {
            const senderName = data.userName || 'Usuário';
            console.log('🔔 [NOTIF] Criando aba DM dinâmica (chat_received) para:', senderName);
            const dynamicChannel: Channel = {
              id: channelId,
              name: senderName,
              type: 'direct',
              icon: ChatBubbleLeftRightIcon,
              unreadCount: 1,
              otherUserId: data.userId,
              otherUserName: senderName,
              otherUserOnline: true
            };
            // Adicionar canal à lista se não existe
            setChannels(prevCh => {
              if (!prevCh.find(c => c.id === channelId)) {
                return [...prevCh, dynamicChannel];
              }
              return prevCh;
            });
            const newTab: ChatTab = { ...dynamicChannel, hasUnread: true };
            return [...prev, newTab];
          }
          // Canais e departamentos: não auto-abrir, só badge na sidebar
          return prev;
        }
      });

      // 🔔 Browser notification (se permitido e configurado)
      const chatSettings = JSON.parse(localStorage.getItem('chat_settings') || '{"enabled":true,"desktop":true}');
      if (chatSettings.enabled && chatSettings.desktop && 'Notification' in window && Notification.permission === 'granted' && !mutedChannels.has(channelId)) {
        const senderName = data.userName || 'Alguém';
        new Notification(`💬 ${senderName} te enviou uma mensagem`, {
          body: 'Clique para ver no FlowChat',
          icon: '/favicon.ico',
          tag: `chat-${channelId}`
        });
      } else if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    };

    socket.on('chat_message_received', handleChatReceived);
    return () => { socket.off('chat_message_received', handleChatReceived); };
  }, [socket, isConnected, session?.usuario?.id, activeTabId, channels, mutedChannels, playNotificationSound]);

  // Polling de mensagens (fallback - intervalo maior pois Socket.IO entrega em tempo real)
  useEffect(() => {
    if (!activeTabId) return;

    const interval = setInterval(() => {
      try {
        refreshMessages(activeTabId, undefined, session?.empresa?.id);
      } catch (error) {
        console.error('❌ Erro ao atualizar mensagens:', error);
      }
    }, 30000); // 30 segundos (fallback, Socket.IO cuida do tempo real)

    return () => clearInterval(interval);
  }, [activeTabId, refreshMessages]);

  // 🟢 Heartbeat - Atualizar status online a cada 30 segundos
  // Um heartbeat por usuário, não por aba: a aba dona envia e divide a resposta.
  // `runWhenHidden` porque parar o heartbeat marcaria o usuário como offline
  // (ONLINE_TIMEOUT = 30s no server.py) só por ter minimizado o navegador.
  useSharedPoll(
    session?.usuario?.id ? `chat-heartbeat:${session.usuario.id}` : null,
    '/api/chat/heartbeat',
    30000,
    (data: any) => {
      // Atualizar lista de usuários online
      if (data?.success && data.onlineUsers) {
        setOnlineUsers(data.onlineUsers);
      }
    },
    {
      method: 'POST',
      body: { userId: session?.usuario?.id },
      runWhenHidden: true,
    }
  );

  // 🔄 Refresh de canais para atualizar status online dos membros
  useEffect(() => {
    if (!session?.empresa?.id || !session?.usuario?.id) return;

    // Atualizar canais a cada 60 segundos para pegar novos status online
    const interval = setInterval(() => {
      if (session.empresa?.id && session.usuario?.id) {
        try {
          loadChannels(session.empresa.id, session.usuario.id);
        } catch (error) {
          console.error('❌ Erro ao atualizar canais:', error);
          // Não propagar erro para não quebrar o polling
        }
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [session?.empresa?.id, session?.usuario?.id, loadChannels]);

  // Criar canal de mensagem direta
  const createDirectMessageChannel = (colaborador: any) => {
    // 🔑 CRÍTICO: Usar sempre usuarioId (str(usuarios._id)) para ambos os lados
    // session.usuario.id = str(usuarios._id)
    // colaborador.usuarioId = str(usuarios._id) do outro usuário (mapeado via email no backend)
    // Isso garante que ambos os usuários criem o MESMO canal DM
    const otherUsuarioId = colaborador.usuarioId || colaborador.userId || colaborador.id;
    const ids = [String(session.usuario.id), String(otherUsuarioId)].sort();
    const dmChannelId = `dm-${ids[0]}-${ids[1]}`;
    
    // Verificar se já existe (em qualquer formato)
    const existingChannel = channels.find(c => c.id === dmChannelId || 
      (c.type === 'direct' && (c.otherUserId === otherUsuarioId || c.otherUserId === colaborador.id)));
    if (existingChannel) {
      openTabForChannel({ ...existingChannel, id: dmChannelId });
      return;
    }

    const newDMChannel: Channel = {
      id: dmChannelId,
      name: colaborador.nome,
      type: 'direct',
      icon: ChatBubbleLeftRightIcon,
      unreadCount: 0,
      otherUserId: otherUsuarioId,
      otherUserName: colaborador.nome,
      otherUserOnline: false
    };

    setChannels(prev => [...prev, newDMChannel]);
    openTabForChannel(newDMChannel);
  };

  // 🖼️ Colar print direto na conversa (Ctrl+V).
  // A imagem NÃO vai embutida na mensagem: o chat guarda cada canal num arquivo
  // JSON que é lido e reescrito inteiro a cada envio — base64 ali dentro faria o
  // arquivo crescer sem limite e travar o canal. Subimos para /api/upload-anexo-local
  // e guardamos só a URL, do mesmo jeito que os anexos de card.
  const handleColarNoInput = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const itens = Array.from(e.clipboardData?.items || []);
    const imagens = itens.filter(i => i.type.startsWith('image/'));
    if (imagens.length === 0) return; // texto normal segue o fluxo padrão

    e.preventDefault();
    setSubindoImagem(true);
    try {
      for (const item of imagens) {
        const arquivo = item.getAsFile();
        if (!arquivo) continue;

        const extensao = (arquivo.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const nome = arquivo.name && arquivo.name !== 'image.png'
          ? arquivo.name
          : `print-${Date.now()}.${extensao}`;

        const form = new FormData();
        form.append('file', arquivo, nome);
        form.append('campoId', 'chat');
        form.append('flowNome', 'FlowChat');
        form.append('cardTitulo', activeTabId || 'geral');

        const res = await fetch('/api/upload-anexo-local', { method: 'POST', body: form });
        const data = await res.json();
        if (data?.success && data.url) {
          setImagensPendentes(prev => [...prev, { name: nome, url: data.url, type: arquivo.type }]);
        } else {
          console.error('❌ [CHAT] Falha ao subir print:', data);
          alert('Não foi possível anexar o print. Tente novamente.');
        }
      }
    } catch (err) {
      console.error('❌ [CHAT] Erro ao colar imagem:', err);
      alert('Erro ao anexar o print.');
    } finally {
      setSubindoImagem(false);
    }
  };

  const handleSendMessage = async () => {
    const temTexto = !!messageInput.trim();
    const temImagem = imagensPendentes.length > 0;
    // Print sem legenda é envio válido
    if ((!temTexto && !temImagem) || !activeTabId || !session?.empresa?.id || isSending) return;

    try {
      setIsSending(true);
      
      const messageData = {
        channelId: activeTabId,
        userId: session.usuario.id,
        userName: session.usuario.nome,
        userEmail: session.usuario.email,
        userAvatar: session.usuario.foto,
        content: messageInput,
        type: 'text',
        mentions: extractMentions(messageInput),
        empresaId: session.empresa.id,
        replyTo: replyingTo?.id,
        attachments: imagensPendentes,
      };
      
      console.log('📤 Enviando mensagem:', messageData);
      if (replyingTo) {
        console.log('↩️ Respondendo à mensagem:', replyingTo);
      }
      
      await sendMessage(messageData as any);

      setMessageInput('');
      setImagensPendentes([]); // prints já foram junto da mensagem
      setReplyingTo(null); // Limpar reply após enviar
      
      // 🔔 MESSENGER: Remover do needsReply ao enviar mensagem (respondeu!)
      if (activeTabId) {
        setNeedsReply(prev => {
          const next = new Set(prev);
          next.delete(activeTabId);
          return next;
        });
      }
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSending(false);
    }
  };

  const handleEditMessage = async () => {
    if (!editInput.trim() || !editingMessage || !session?.usuario?.id) return;

    try {
      const isDev = window.location.port === '5173' || window.location.port === '5174';
      const apiUrl = isDev ? 'http://localhost:5001' : `${window.location.protocol}//${window.location.hostname}`;
      const response = await fetch(`${apiUrl}/api/chat/messages/${editingMessage.id || editingMessage._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: session.usuario.id,
          content: editInput,
          channelId: activeTabId,
          empresaId: session.empresa?.id || 'default'
        })
      });

      if (!response.ok) throw new Error('Erro ao editar mensagem');

      // Atualizar mensagens do servidor
      if (activeTabId) {
        await refreshMessages(activeTabId, session.usuario.id);
      }

      setEditingMessage(null);
      setEditInput('');
    } catch (error) {
      console.error('❌ Erro ao editar mensagem:', error);
      alert('Erro ao editar mensagem. Tente novamente.');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!session?.usuario?.id) return;
    
    const confirmed = window.confirm('Tem certeza que deseja excluir esta mensagem?');
    if (!confirmed) return;

    try {
      const isDev = window.location.port === '5173' || window.location.port === '5174';
      const apiUrl = isDev ? 'http://localhost:5001' : `${window.location.protocol}//${window.location.hostname}`;
      const response = await fetch(`${apiUrl}/api/chat/messages/${messageId}?userId=${session.usuario.id}&channelId=${activeTabId}&empresaId=${session.empresa?.id || 'default'}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Erro ao excluir mensagem');

      // Atualizar mensagens do servidor
      if (activeTabId) {
        await refreshMessages(activeTabId, session.usuario.id);
      }
    } catch (error) {
      console.error('❌ Erro ao excluir mensagem:', error);
      alert('Erro ao excluir mensagem. Tente novamente.');
    }
  };

  const extractMentions = (text: string): string[] => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    
    return mentions;
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} ${timeStr}`;
  };

  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sessionEmailLower = (session.usuario.email || '').toLowerCase();
  const otherColaboradores = colaboradores.filter(c => {
    const colabEmail = (c.email || '').toLowerCase();
    return c.id !== session.usuario.id && colabEmail !== sessionEmailLower;
  });
  const activeTab = openTabs.find(tab => tab.id === activeTabId);

  // Função para enriquecer membros com status online - MEMOIZADA
  const enhanceMembers = useCallback((members: any[]) => {
    const sessionEmail = sessionEmailLower;
    const sessionId = String(session.usuario.id || '');

    const sessionNameLower = (session.usuario.nome || '').trim().toLowerCase();
    const enriched = (members || []).map(member => {
      const memberEmail = (member.email || '').toLowerCase();
      const memberId = String(member.id || '');
      // CRÍTICO: userId é o ID da tabela 'users' (usado para presença online)
      // id é o ID da tabela 'colaboradores' (usado para exibição)
      const memberUserId = String(member.userId || member.id || '');
      const memberNameLower = (member.nome || '').trim().toLowerCase();
      const isCurrentUser =
        memberEmail === sessionEmail ||
        memberUserId === sessionId ||
        memberId === sessionId ||
        (!!memberNameLower && memberNameLower === sessionNameLower);

      // Se o backend já forneceu o status online, usar isso (garante filtro por canal)
      // Caso contrário, verificar presença usando userId global
      const isOnline = member.online !== undefined 
        ? member.online 
        : (isCurrentUser ? true : onlineUsers.includes(memberUserId));
      
      console.log(`🔍 Checando online: ${member.nome} (colab:${memberId}, user:${memberUserId}) = ${isOnline}`, { 
        backendOnline: member.online,
        globalOnlineUsers: onlineUsers 
      });

      return {
        ...member,
        online: isOnline,
        foto: member.foto || (isCurrentUser ? session.usuario.foto : member.foto)
      };
    });

    // Não adicionar o usuário atual automaticamente
    // O backend já retorna os membros corretos para cada canal/departamento
    return enriched;
  }, [onlineUsers, sessionEmailLower, session.usuario]);

  // Buscar membros do canal ativo - MEMOIZADO para reagir a mudanças em onlineUsers
  const channelMembers = useMemo(() => {
    if (!activeTab) return [];
    
    console.log('🔄 Recalculando channelMembers...', { 
      activeTab: activeTab.name, 
      type: activeTab.type,
      onlineUsers,
      membersDetails: activeTab.membersDetails?.length,
      membersDetailsComplete: activeTab.membersDetails
    });
    
    // Se o canal tem membersDetails (do backend), usar isso
    if (activeTab.membersDetails && activeTab.membersDetails.length > 0) {
      console.log('✅ Usando membersDetails do backend:', activeTab.name, activeTab.membersDetails.length, 'membros');
      console.log('📋 Membros completos:', activeTab.membersDetails);
      return enhanceMembers(activeTab.membersDetails);
    }
    
    console.log('⚠️ Canal sem membersDetails, usando fallback para:', activeTab.type);
    
    // Fallback para canais sem membersDetails
    if (activeTab.type === 'department') {
      return enhanceMembers(colaboradores.filter(c => c.departamentoId === activeTab.departmentId));
    } else if (activeTab.type === 'general') {
      return enhanceMembers(otherColaboradores);
    } else if (activeTab.type === 'direct') {
      // Para DMs, mostrar os dois participantes (eu + outro usuário)
      const dmMembers: any[] = [];
      // Outro usuário da DM
      if (activeTab.otherUserId) {
        const otherColab = colaboradores.find(c => c.id === activeTab.otherUserId);
        if (otherColab) {
          dmMembers.push(otherColab);
        } else {
          dmMembers.push({
            id: activeTab.otherUserId,
            nome: activeTab.otherUserName || 'Usuário',
            email: '',
            online: activeTab.otherUserOnline || false,
          });
        }
      }
      // Eu mesmo
      const me = colaboradores.find(c => c.id === session.usuario?.id);
      if (me) {
        dmMembers.push(me);
      } else if (session.usuario) {
        dmMembers.push({
          id: session.usuario.id,
          nome: session.usuario.nome || 'Eu',
          email: session.usuario.email || '',
          online: true,
        });
      }
      return enhanceMembers(dmMembers);
    }
    
    return enhanceMembers([]);
  }, [activeTab, enhanceMembers, colaboradores, otherColaboradores, session]);

  return (
    <div className="flex h-full bg-gray-50 relative">
      {/* Sidebar - Lista de Canais */}
      <div className={`${
        showChannelList ? 'w-full sm:w-64' : 'w-0'
      } bg-gradient-to-b from-gray-900 to-gray-800 text-white transition-all duration-300 overflow-hidden flex flex-col absolute sm:relative h-full z-50 sm:z-auto`}>
        {/* Header da Sidebar */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-bold truncate">{session.empresa?.nome || 'FlowChat'}</h2>
            <button
              onClick={() => setShowChannelList(false)}
              className="sm:hidden p-2 hover:bg-white/10 rounded"
              aria-label="Fechar menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Busca */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
            <input
              type="text"
              placeholder="Buscar canais..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
          </div>
        </div>

        {/* Lista de Canais */}
        <div className="flex-1 overflow-y-auto">
          {/* Loading */}
          {channels.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-white/50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-3"></div>
              <p className="text-sm">Carregando canais...</p>
            </div>
          ) : (
            <>
              {/* Canais */}
              <div className="px-3 py-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-white/50">CANAIS</div>
                  <button className="p-0.5 hover:bg-white/10 rounded">
                    <PlusIcon className="h-4 w-4 text-white/50" />
                  </button>
                </div>
            
            {/* Canal Geral */}
            {filteredChannels.filter(c => c.type === 'general').map(channel => (
              <button
                key={channel.id}
                onClick={() => openTabForChannel(channel)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  activeTabId === channel.id
                    ? 'bg-white/20 text-white'
                    : 'hover:bg-white/10 text-white/80'
                }`}
              >
                <HashtagIcon className="h-4 w-4" />
                <span className="flex-1 text-left text-sm font-medium truncate">{channel.name}</span>
                {channel.unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {channel.unreadCount}
                  </span>
                )}
              </button>
            ))}

            {/* Canais de Fluxos */}
            {filteredChannels.filter(c => c.type === 'flow').map(channel => (
              <button
                key={channel.id}
                onClick={() => openTabForChannel(channel)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  activeTabId === channel.id
                    ? 'bg-white/20 text-white'
                    : 'hover:bg-white/10 text-white/80'
                }`}
              >
                <HashtagIcon className="h-4 w-4" />
                <span className="flex-1 text-left text-sm font-medium truncate">{channel.name}</span>
                {channel.unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {channel.unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Departamentos */}
          <div className="px-3 py-2 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-white/50">DEPARTAMENTOS</div>
            </div>
            
            {filteredChannels.filter(c => c.type === 'department').map(channel => (
              <button
                key={channel.id}
                onClick={() => openTabForChannel(channel)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  activeTabId === channel.id
                    ? 'bg-white/20 text-white'
                    : 'hover:bg-white/10 text-white/80'
                }`}
              >
                <BuildingOfficeIcon className="h-4 w-4" />
                <span className="flex-1 text-left text-sm font-medium truncate">{channel.name}</span>
                {channel.unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {channel.unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Mensagens Diretas */}
          <div className="px-3 py-2 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-white/50">MENSAGENS DIRETAS</div>
            </div>
            
            {filteredChannels.filter(c => c.type === 'direct').map(channel => (
              <button
                key={channel.id}
                onClick={() => openTabForChannel(channel)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  activeTabId === channel.id
                    ? 'bg-white/20 text-white'
                    : 'hover:bg-white/10 text-white/80'
                }`}
              >
                <div className="relative">
                  <div className={`h-6 w-6 rounded-full bg-gradient-to-br ${getUserColor(channel.name)} flex items-center justify-center text-white text-xs font-semibold`}>
                    {channel.name.charAt(0).toUpperCase()}
                  </div>
                  {channel.otherUserOnline && (
                    <div className="absolute bottom-0 right-0 h-2 w-2 bg-green-400 border border-gray-900 rounded-full"></div>
                  )}
                </div>
                <span className="flex-1 text-left text-sm font-medium truncate">{channel.name}</span>
                {channel.unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {channel.unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
            </>
          )}
        </div>

        {/* User Profile */}
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className={`h-9 w-9 rounded-full bg-gradient-to-br ${getUserColor(session.usuario.nome)} flex items-center justify-center text-white text-sm font-semibold`}>
                {session.usuario.nome.charAt(0).toUpperCase()}
              </div>
              <div className="absolute bottom-0 right-0 h-2.5 w-2.5 bg-green-400 border-2 border-gray-900 rounded-full"></div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{session.usuario.nome}</div>
              <div className="text-xs text-white/60 truncate">{session.usuario.email}</div>
            </div>
            <button className="p-1 hover:bg-white/10 rounded">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Área Principal - Abas e Chat */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Barra de Abas */}
        {openTabs.length > 0 && (
          <div className="bg-white border-b border-gray-200 flex items-center overflow-x-auto scrollbar-hide">
            {!showChannelList && (
              <button
                onClick={() => setShowChannelList(true)}
                className="p-2 sm:p-3 hover:bg-gray-100 border-r border-gray-200 flex-shrink-0"
                aria-label="Menu"
              >
                <Bars3Icon className="h-5 w-5 text-gray-600" />
              </button>
            )}
            
            {openTabs.map(tab => {
              const isActive = activeTabId === tab.id;
              const hasUnread = tab.hasUnread && !isActive;
              const unreadCount = tab.unreadCount || 0;
              const isWaitingReply = needsReply.has(tab.id);
              
              return (
                <div
                  key={tab.id}
                  className={`relative flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 border-r border-gray-200 transition-all min-w-[120px] sm:min-w-[160px] max-w-[150px] sm:max-w-[200px] ${
                    isActive && isWaitingReply
                      ? 'bg-gradient-to-r from-orange-100 to-amber-100 border-b-2 border-orange-500'
                      : isActive
                      ? 'bg-gray-50 border-b-2 border-gray-800' 
                      : isWaitingReply
                        ? 'bg-gradient-to-r from-orange-200 to-red-100 border-b-2 border-red-500 hover:from-orange-300 hover:to-red-200 chat-tab-needs-reply'
                        : hasUnread
                        ? 'bg-gray-100 border-b-2 border-orange-400 hover:bg-gray-200'
                        : 'hover:bg-gray-50'
                  }`}
                >
                  {/* 🔔 Indicador pulsante para aba aguardando resposta */}
                  {isWaitingReply && !isActive && (
                    <div className="absolute -top-1 -left-1 h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setActiveTabId(tab.id);
                      loadMessages(tab.id, { userId: session?.usuario?.id, empresaId: session?.empresa?.id });
                    }}
                    className="flex items-center gap-2 flex-1 min-w-0"
                  >
                    {tab.icon && <tab.icon className={`h-4 w-4 flex-shrink-0 ${isWaitingReply ? 'text-red-600' : hasUnread ? 'text-gray-800' : 'text-gray-600'}`} />}
                    <span className={`flex-1 text-xs sm:text-sm font-medium truncate ${
                      isWaitingReply ? 'text-red-700 font-bold' : hasUnread ? 'text-gray-900 font-bold' : 'text-gray-700'
                    }`}>{tab.name}</span>
                    
                    {(hasUnread || isWaitingReply) && unreadCount > 0 && (
                      <span className={`text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 animate-pulse min-w-[18px] text-center ${isWaitingReply ? 'bg-red-600' : 'bg-red-500'}`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    {isWaitingReply && unreadCount === 0 && (
                      <span className="bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 animate-pulse">
                        !
                      </span>
                    )}
                    {hasUnread && !isWaitingReply && unreadCount === 0 && (
                      <div className="h-2.5 w-2.5 bg-orange-400 rounded-full flex-shrink-0 animate-pulse"></div>
                    )}
                  </button>
                  
                  <button
                    onClick={(e) => closeTab(tab.id, e)}
                    className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded"
                  >
                    <XMarkIcon className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Área de Chat */}
        {activeTab ? (
          <div className="flex-1 flex overflow-hidden">
            {/* Mensagens */}
            <div className="flex-1 flex flex-col">
              {/* Header do Canal */}
              <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-2 sm:py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <button
                      onClick={() => setShowChannelList(true)}
                      className="sm:hidden p-1 hover:bg-gray-100 rounded-lg flex-shrink-0"
                      aria-label="Menu"
                    >
                      <Bars3Icon className="h-5 w-5 text-gray-600" />
                    </button>
                    {activeTab.icon && <activeTab.icon className="h-4 sm:h-5 w-4 sm:w-5 text-gray-600 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{activeTab.name}</h3>
                      <p className="text-xs text-gray-500 hidden sm:block">
                        {channelMembers.length} {channelMembers.length === 1 ? 'membro' : 'membros'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                    {/* 🔔 Botão Mutar/Desmutar Canal */}
                    <button 
                      onClick={() => activeTab?.id && toggleMuteChannel(activeTab.id)}
                      className={`p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors ${
                        activeTab?.id && mutedChannels.has(activeTab.id) ? 'text-red-500' : 'text-gray-600'
                      }`}
                      title={activeTab?.id && mutedChannels.has(activeTab.id) ? 'Desmutar notificações' : 'Mutar notificações'}
                    >
                      {activeTab?.id && mutedChannels.has(activeTab.id) ? (
                        <BellSlashIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                      ) : (
                        <BellIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                      )}
                    </button>
                    {/* 📱 Botão Membros Mobile */}
                    <button 
                      onClick={() => setShowMembersPanel(!showMembersPanel)}
                      className="lg:hidden p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg touch-manipulation"
                      title="Membros"
                    >
                      <UsersIcon className="h-4 sm:h-5 w-4 sm:w-5 text-gray-600" />
                    </button>
                    {/* 🖥️ Botão Toggle Membros Desktop */}
                    <button 
                      onClick={() => setShowMembersPanelDesktop(!showMembersPanelDesktop)}
                      className={`hidden lg:flex p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-all ${
                        showMembersPanelDesktop ? 'bg-gray-200 text-gray-800' : 'text-gray-600'
                      }`}
                      title={showMembersPanelDesktop ? 'Ocultar membros' : 'Mostrar membros'}
                    >
                      <UsersIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Lista de Mensagens */}
              <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-3 sm:space-y-4 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <ChatBubbleLeftRightIcon className="h-16 w-16 mb-3" />
                    <p className="text-lg font-medium">Nenhuma mensagem ainda</p>
                    <p className="text-sm">Seja o primeiro a enviar uma mensagem!</p>
                  </div>
                ) : (
                  messages.map(message => {
                    // Buscar mensagem original se for reply (tentar por _id ou id)
                    const repliedMessage = message.replyTo 
                      ? messages.find(m => m.id === message.replyTo || m._id === message.replyTo)
                      : null;
                    
                    // 🐛 Debug de reply
                    if (message.replyTo) {
                      console.log('📨 Mensagem com reply:', {
                        messageId: message.id || message._id,
                        replyTo: message.replyTo,
                        repliedMessageFound: !!repliedMessage,
                        allMessageIds: messages.map(m => ({ id: m.id, _id: m._id }))
                      });
                    }
                    
                    // 🖼️ Buscar foto do usuário (testar múltiplos campos)
                    const sessionEmail = sessionEmailLower;
                    const sessionId = String(session.usuario.id || '');
                    const messageEmail = (message.userEmail || '').toLowerCase();

                    const messageNameLower = (message.userName || '').trim().toLowerCase();
                    const userMember = channelMembers.find(m => {
                      const memberEmail = (m.email || '').toLowerCase();
                      const memberNameLower = (m.nome || '').trim().toLowerCase();
                      return (
                        m.id === message.userId ||
                        m.id === message.userName ||
                        m.nome === message.userName ||
                        (messageEmail && memberEmail === messageEmail) ||
                        (messageNameLower && memberNameLower.startsWith(messageNameLower))
                      );
                    });

                    const isCurrentUser =
                      messageEmail === sessionEmail ||
                      message.userId === sessionId ||
                      message.userName === session.usuario.nome;

                    const userPhoto =
                      userMember?.foto ||
                      message.userAvatar ||
                      (isCurrentUser ? session.usuario.foto : undefined);
                    
                    // Debug: Log se não encontrou foto (apenas uma vez)
                    if (!userPhoto && channelMembers.length > 0 && !(window as any)._photoDebugLogged) {
                      (window as any)._photoDebugLogged = true;
                      console.log('🔍 Foto não encontrada para:', {
                        messageUserId: message.userId,
                        messageUserName: message.userName,
                        messageUserEmail: message.userEmail,
                        channelMembers: channelMembers.map(m => ({ id: m.id, email: m.email, nome: m.nome, hasFoto: !!m.foto }))
                      });
                    }
                    
                    const displayName = userMember?.nome || message.userName || session.usuario.nome;

                    return (
                      <div 
                        key={message.id} 
                        className="flex gap-2 sm:gap-3 group relative hover:bg-gray-100/50 -mx-1 sm:-mx-2 px-1 sm:px-2 py-1 rounded-lg transition-colors"
                      >
                        {/* Avatar com foto ou iniciais */}
                        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full flex-shrink-0 overflow-hidden">
                          {userPhoto ? (
                            <img 
                              src={userPhoto} 
                              alt={message.userName}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className={`h-full w-full bg-gradient-to-br ${getUserColor(message.userName)} flex items-center justify-center text-white text-sm font-semibold`}>
                              {message.userName.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-semibold text-gray-900 text-sm">{displayName}</span>
                            <span className="text-xs text-gray-500">{formatTime(message.timestamp)}</span>
                          </div>
                          
                          {/* Mensagem sendo respondida */}
                          {repliedMessage && (
                            <div className="mb-2 pl-2 sm:pl-3 border-l-4 border-gray-400 bg-gradient-to-r from-gray-100 to-transparent rounded py-2 px-3">
                              <div className="text-xs text-gray-700 font-semibold mb-1 flex items-center gap-1">
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                                {repliedMessage.userName}
                              </div>
                              <div className="text-xs text-gray-700 line-clamp-2">
                                {repliedMessage.content}
                              </div>
                            </div>
                          )}
                          
                          {message.content && (
                            <div className="text-sm text-gray-700 break-words">{message.content}</div>
                          )}

                          {/* 🖼️ Prints da mensagem — clique abre em tamanho real */}
                          {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              {message.attachments.map((anexo, idx) => (
                                anexo?.type?.startsWith('image/') ? (
                                  <a
                                    key={`${anexo.url}-${idx}`}
                                    href={anexo.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={anexo.name}
                                  >
                                    <img
                                      src={anexo.url}
                                      alt={anexo.name}
                                      className="max-h-56 max-w-[280px] rounded-lg border border-gray-200 hover:border-gray-400 transition-colors cursor-zoom-in"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    key={`${anexo.url}-${idx}`}
                                    href={anexo.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    {anexo.name}
                                  </a>
                                )
                              ))}
                            </div>
                          )}

                          {/* Indicador de mensagem editada */}
                          {message.edited && (
                            <span className="text-xs text-gray-400 italic mt-1">editado</span>
                          )}
                        </div>
                        
                        {/* Botões de ação (Reply, Edit, Delete) */}
                        <div className="absolute right-1 sm:right-2 top-1 sm:top-2 opacity-0 sm:group-hover:opacity-100 active:opacity-100 transition-opacity flex gap-1">
                          {/* Botão de Reply */}
                          <button
                            onClick={() => setReplyingTo(message)}
                            className="p-1 sm:p-1.5 hover:bg-gray-200 rounded text-gray-600 text-xs flex items-center gap-1 touch-manipulation"
                            title="Responder"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                            </svg>
                            <span className="hidden sm:inline">Responder</span>
                          </button>
                          
                          {/* Botão de Editar (se permitido) */}
                          {message.canEdit && (
                            <button
                              onClick={() => {
                                setEditingMessage(message);
                                setEditInput(message.content);
                              }}
                              className="p-1 sm:p-1.5 hover:bg-blue-100 rounded text-blue-600 text-xs flex items-center gap-1 touch-manipulation"
                              title="Editar"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              <span className="hidden sm:inline">Editar</span>
                            </button>
                          )}
                          
                          {/* Botão de Excluir (se permitido) */}
                          {message.canDelete && (
                            <button
                              onClick={() => handleDeleteMessage(message.id || message._id || '')}
                              className="p-1 sm:p-1.5 hover:bg-red-100 rounded text-red-600 text-xs flex items-center gap-1 touch-manipulation"
                              title="Excluir"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span className="hidden sm:inline">Excluir</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input de Mensagem */}
              <div className="bg-white border-t border-gray-200 p-2 sm:p-4 pb-safe">
                {/* Preview de Reply */}
                {replyingTo && (
                  <div className="mb-2 sm:mb-3 p-3 bg-gradient-to-r from-gray-100 to-gray-50 border-l-4 border-gray-500 rounded-lg flex items-start justify-between shadow-sm">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-700 font-semibold mb-1 flex items-center gap-1">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        Respondendo a {replyingTo.userName}
                      </div>
                      <div className="text-sm text-gray-700 truncate">
                        {replyingTo.content}
                      </div>
                    </div>
                    <button
                      onClick={() => setReplyingTo(null)}
                      className="ml-2 p-1 hover:bg-gray-200 rounded-lg text-gray-600 transition-colors"
                      title="Cancelar resposta"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
                
                {/* Preview de Edit */}
                {editingMessage && (
                  <div className="mb-2 sm:mb-3 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-500 rounded-lg flex items-start justify-between shadow-sm">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-blue-700 font-semibold mb-2 flex items-center gap-1">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editando mensagem
                      </div>
                      <textarea
                        value={editInput}
                        onChange={(e) => setEditInput(e.target.value)}
                        className="w-full p-2 border border-blue-300 rounded-md text-sm resize-none"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={handleEditMessage}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                        >
                          Salvar
                        </button>
                        <button
                          onClick={() => {
                            setEditingMessage(null);
                            setEditInput('');
                          }}
                          className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-end gap-1 sm:gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg overflow-visible relative">
                    {/* 🖼️ Prints colados, aguardando envio */}
                    {(imagensPendentes.length > 0 || subindoImagem) && (
                      <div className="flex flex-wrap items-center gap-2 px-3 pt-2">
                        {imagensPendentes.map((img, idx) => (
                          <div key={`${img.url}-${idx}`} className="relative group">
                            <img
                              src={img.url}
                              alt={img.name}
                              className="h-16 w-16 object-cover rounded-lg border border-gray-300"
                            />
                            <button
                              onClick={() => setImagensPendentes(prev => prev.filter((_, i) => i !== idx))}
                              className="absolute -top-1.5 -right-1.5 bg-gray-800 text-white rounded-full p-0.5 shadow hover:bg-black transition-colors"
                              title="Remover print"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        {subindoImagem && (
                          <div className="h-16 w-16 rounded-lg border border-dashed border-gray-300 flex items-center justify-center">
                            <div className="h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    )}
                    <textarea
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onPaste={handleColarNoInput}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder={`Mensagem em ${activeTab.name}... (Ctrl+V cola print)`}
                      className="w-full px-3 sm:px-4 pt-3 pb-1 bg-transparent border-none focus:outline-none resize-none text-sm sm:text-base"
                      rows={1}
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                    />
                    
                    {/* Emoji Picker */}
                    {showEmojiPicker && (
                      <EmojiPicker
                        onSelect={(emoji) => setMessageInput(prev => prev + emoji)}
                        onClose={() => setShowEmojiPicker(false)}
                      />
                    )}
                    
                    <div className="flex items-center gap-2 px-3 pb-2 bg-gray-50">
                      <button
                        onClick={() => {
                          console.log('🎭 Emoji button clicked, current state:', showEmojiPicker);
                          setShowEmojiPicker(!showEmojiPicker);
                        }}
                        className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                        title="Adicionar emoji"
                      >
                        <FaceSmileIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={(!messageInput.trim() && imagensPendentes.length === 0) || isSending || subindoImagem}
                    className="p-2.5 sm:p-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 active:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation flex-shrink-0"
                    aria-label="Enviar mensagem"
                  >
                    {isSending ? (
                      <div className="h-5 w-5 sm:h-6 sm:w-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <PaperAirplaneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Painel Lateral - Membros (escondido no mobile, togglable no desktop) */}
            {showMembersPanelDesktop && (
              <div className="hidden lg:flex w-72 bg-white border-l border-gray-200 flex-col overflow-hidden transition-all duration-300">
              {/* Header fixo */}
              <div className="p-3 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900">
                    Membros
                  </h3>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      <span className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></span>
                      {channelMembers.filter(m => m.online).length}
                    </span>
                    <span className="text-gray-400">/</span>
                    <span className="text-gray-500">{channelMembers.length}</span>
                  </div>
                </div>

                {/* Busca de membros */}
                <div className="relative mb-2">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar membro..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400 transition-colors"
                  />
                  {memberSearch && (
                    <button onClick={() => setMemberSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                      <XMarkIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>

                {/* Filtros */}
                <div className="flex gap-1">
                  {(['all', 'online', 'offline'] as const).map(filter => (
                    <button
                      key={filter}
                      onClick={() => setMemberFilter(filter)}
                      className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                        memberFilter === filter
                          ? filter === 'online'
                            ? 'bg-green-100 text-green-700 ring-1 ring-green-300'
                            : filter === 'offline'
                            ? 'bg-gray-200 text-gray-700 ring-1 ring-gray-300'
                            : 'bg-gray-200 text-gray-800 ring-1 ring-gray-400'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {filter === 'all' ? 'Todos' : filter === 'online' ? '🟢 Online' : '⚫ Offline'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lista de membros com scroll */}
              <div className="flex-1 overflow-y-auto p-2">
                {(() => {
                  // Aplicar filtros
                  let filtered = channelMembers.filter(m => {
                    const matchSearch = !memberSearch || m.nome.toLowerCase().includes(memberSearch.toLowerCase()) || (m.email || '').toLowerCase().includes(memberSearch.toLowerCase());
                    const matchFilter = memberFilter === 'all' || (memberFilter === 'online' && m.online) || (memberFilter === 'offline' && !m.online);
                    return matchSearch && matchFilter;
                  });

                  // Ordenar: online primeiro, depois alfabético
                  filtered.sort((a, b) => {
                    if (a.online && !b.online) return -1;
                    if (!a.online && b.online) return 1;
                    return (a.nome || '').localeCompare(b.nome || '');
                  });

                  const isAdmin = (m: any) => m.role === 'admin' || m.role === 'administrator';
                  const admins = filtered.filter(isAdmin);
                  const participants = filtered.filter(m => !isAdmin(m));

                  if (filtered.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                        <UsersIcon className="h-10 w-10 mb-2" />
                        <p className="text-xs">Nenhum membro encontrado</p>
                      </div>
                    );
                  }

                  const renderMember = (member: any, badgeText?: string, badgeColor?: string) => (
                    <button
                      key={member.id}
                      onClick={() => createDirectMessageChannel(member)}
                      className="w-full flex items-center gap-2.5 p-2 hover:bg-gray-50 rounded-lg transition-all group/member"
                      title={`Enviar mensagem para ${member.nome}`}
                    >
                      <div className="relative flex-shrink-0">
                        <div className="h-8 w-8 rounded-full overflow-hidden ring-2 ring-transparent group-hover/member:ring-gray-300 transition-all">
                          {member.foto ? (
                            <img src={member.foto} alt={member.nome} className="h-full w-full object-cover" />
                          ) : (
                            <div className={`h-full w-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-br ${getUserColor(member.nome)}`}>
                              {member.nome.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${
                          member.online ? 'bg-green-400' : 'bg-gray-300'
                        }`}></div>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1">
                          <span className={`text-sm font-medium truncate ${
                            member.online ? 'text-gray-900' : 'text-gray-500'
                          }`}>{member.nome}</span>
                          {badgeText && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${badgeColor || 'bg-gray-100 text-gray-600'}`}>
                              {badgeText}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          {member.online ? 'Online agora' : member.cargo || member.email || 'Offline'}
                        </div>
                      </div>
                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-300 group-hover/member:text-gray-600 transition-colors flex-shrink-0" />
                    </button>
                  );

                  return (
                    <>
                      {admins.length > 0 && (
                        <div className="mb-2">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">Administradores</div>
                          <div className="space-y-0.5">
                            {admins.map(m => renderMember(m, 'Admin', 'bg-amber-100 text-amber-700'))}
                          </div>
                        </div>
                      )}
                      {participants.length > 0 && (
                        <div>
                          {admins.length > 0 && <div className="border-t border-gray-100 my-2"></div>}
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">
                            {activeTab?.type === 'flow' ? 'Participantes' : 'Membros'}
                          </div>
                          <div className="space-y-0.5">
                            {participants.map(m => renderMember(m))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <ChatBubbleLeftRightIcon className="h-20 w-20 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Bem-vindo ao FlowChat! ⚡</h3>
              <p className="text-sm mb-4">Selecione um canal na barra lateral para começar a conversar</p>
              <button
                onClick={() => setShowChannelList(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 text-white rounded-lg shadow-md hover:bg-gray-900 transition-colors"
              >
                <Bars3Icon className="h-5 w-5" />
                Ver canais
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 📱 PAINEL MODAL DE MEMBROS (Mobile) */}
      {showMembersPanel && activeTab && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
          onClick={() => setShowMembersPanel(false)}
        >
          <div 
            className="bg-white w-full sm:max-w-md max-h-[90vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-3 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-semibold text-lg">Membros</h3>
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="flex items-center gap-1 text-green-300 font-medium">
                      <span className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></span>
                      {channelMembers.filter(m => m.online).length}
                    </span>
                    <span className="text-white/50">/</span>
                    <span className="text-white/70">{channelMembers.length}</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowMembersPanel(false)}
                  className="p-1 text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Busca mobile */}
              <div className="relative mb-2">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                <input
                  type="text"
                  placeholder="Buscar membro..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm placeholder-white/50 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                />
              </div>

              {/* Filtros mobile */}
              <div className="flex gap-1">
                {(['all', 'online', 'offline'] as const).map(filter => (
                  <button
                    key={filter}
                    onClick={() => setMemberFilter(filter)}
                    className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                      memberFilter === filter
                        ? 'bg-white text-gray-800'
                        : 'text-white/70 hover:bg-white/10'
                    }`}
                  >
                    {filter === 'all' ? 'Todos' : filter === 'online' ? '🟢 Online' : '⚫ Offline'}
                  </button>
                ))}
              </div>
            </div>

            {/* Lista de Membros mobile */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 pb-safe"
                 style={{ minHeight: '200px', maxHeight: 'calc(90vh - 180px)' }}
            >
              {(() => {
                let filtered = channelMembers.filter(m => {
                  const matchSearch = !memberSearch || m.nome.toLowerCase().includes(memberSearch.toLowerCase()) || (m.email || '').toLowerCase().includes(memberSearch.toLowerCase());
                  const matchFilter = memberFilter === 'all' || (memberFilter === 'online' && m.online) || (memberFilter === 'offline' && !m.online);
                  return matchSearch && matchFilter;
                });
                filtered.sort((a, b) => {
                  if (a.online && !b.online) return -1;
                  if (!a.online && b.online) return 1;
                  return (a.nome || '').localeCompare(b.nome || '');
                });

                const isAdmin = (m: any) => m.role === 'admin' || m.role === 'administrator';
                const admins = filtered.filter(isAdmin);
                const participants = filtered.filter(m => !isAdmin(m));

                if (filtered.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                      <UsersIcon className="h-12 w-12 mb-2" />
                      <p className="text-sm">Nenhum membro encontrado</p>
                    </div>
                  );
                }

                const renderMobileMember = (member: any, badgeText?: string, badgeColor?: string) => (
                  <button
                    key={member.id}
                    onClick={() => {
                      createDirectMessageChannel(member);
                      setShowMembersPanel(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 active:bg-gray-100 rounded-xl transition-colors touch-manipulation"
                  >
                    <div className="relative flex-shrink-0">
                      <div className="h-11 w-11 rounded-full overflow-hidden">
                        {member.foto ? (
                          <img src={member.foto} alt={member.nome} className="h-full w-full object-cover" />
                        ) : (
                          <div className={`h-full w-full flex items-center justify-center text-white text-lg font-semibold bg-gradient-to-br ${getUserColor(member.nome)}`}>
                            {member.nome.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ${
                        member.online ? 'bg-green-400' : 'bg-gray-300'
                      }`}></div>
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-base font-medium truncate ${
                          member.online ? 'text-gray-900' : 'text-gray-500'
                        }`}>{member.nome}</span>
                        {badgeText && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${badgeColor || 'bg-gray-100 text-gray-600'}`}>
                            {badgeText}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400 truncate">
                        {member.online ? 'Online agora' : member.cargo || member.email || 'Offline'}
                      </div>
                    </div>
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-300 flex-shrink-0" />
                  </button>
                );

                return (
                  <>
                    {admins.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-3 mb-1.5">👑 Administradores</div>
                        <div className="space-y-0.5">
                          {admins.map(m => renderMobileMember(m, 'Admin', 'bg-amber-100 text-amber-700'))}
                        </div>
                      </div>
                    )}
                    {participants.length > 0 && (
                      <div>
                        {admins.length > 0 && <div className="border-t border-gray-100 my-2"></div>}
                        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-3 mb-1.5">
                          {activeTab.type === 'flow' ? 'Participantes' : 'Membros'}
                        </div>
                        <div className="space-y-0.5">
                          {participants.map(m => renderMobileMember(m))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatImproved;
